/**
 * Location-derived building energy intensities for cost / electrification context.
 * Falls back to static defaults when sector data or denominators are missing.
 */

import type { BuildingsEnergyData } from './buildingsEnergyDataService';
import { getNaturalGasConsumptionBySector, getElectricityConsumptionBySector } from './buildingsEnergyDataService';

/** Keep in sync with buildingsCostModel AVG_THERMS_* when adjusting defaults. */
export const FALLBACK_RES_THERMS_PER_HOUSEHOLD = 400;
export const FALLBACK_RES_KWH_PER_HOUSEHOLD = 11000;
export const FALLBACK_COM_THERMS_PER_EQUIVALENT_UNIT = 3000;
export const FALLBACK_COM_KWH_PER_EQUIVALENT_UNIT = 50000;

/** Proxy commercial “establishment” count when sqft / establishment counts are unavailable. */
export const COMMERCIAL_STOCK_POP_DIVISOR = 6;

export interface LocationBuildingEnergyIntensityOptions {
  population: number;
  housingUnits?: number | null;
  /** When provided, commercial intensities are per sq ft. */
  commercialFloorAreaSqft?: number | null;
  /** When provided (and no sqft), commercial intensities are per establishment. */
  commercialEstablishmentCount?: number | null;
}

export interface LocationBuildingEnergyIntensity {
  referenceYear: number;
  dataSource: 'location' | 'fallback';
  housingUnits: number;
  residentialKwhPerHousehold: number;
  residentialThermsPerHousehold: number;
  commercialKwhPerSqft: number | null;
  commercialThermsPerSqft: number | null;
  commercialKwhPerEstablishment: number | null;
  commercialThermsPerEstablishment: number | null;
  /** Synthetic commercial stock for $/unit CAPEX when only population is known. */
  commercialEquivalentUnits: number;
  /** Values passed to pathway electrification context (therms per “unit”). */
  avgThermsPerCommercialEquivalentUnit: number;
  avgKwhPerCommercialEquivalentUnit: number;
}

function max1(n: number): number {
  return Math.max(1, n);
}

/**
 * Derive residential and commercial intensities from Buildings page data.
 */
export function computeLocationBuildingEnergyIntensity(
  buildingsData: BuildingsEnergyData | null | undefined,
  referenceYear: number,
  options: LocationBuildingEnergyIntensityOptions
): LocationBuildingEnergyIntensity {
  const pop = Math.max(0, options.population || 0);
  const housingUnits = Math.max(
    1,
    options.housingUnits != null && options.housingUnits > 0
      ? options.housingUnits
      : pop > 0
        ? pop / 2.5
        : 1
  );

  const { resTherms, comTherms } = getNaturalGasConsumptionBySector(buildingsData, referenceYear);
  const { resKwh, comKwh } = getElectricityConsumptionBySector(buildingsData, referenceYear);
  const hasGas = resTherms + comTherms > 1e-6;
  const hasElec = resKwh + comKwh > 1e-6;

  const sqft = options.commercialFloorAreaSqft;
  const establishments =
    options.commercialEstablishmentCount != null && options.commercialEstablishmentCount > 0
      ? Math.round(options.commercialEstablishmentCount)
      : null;

  let commercialEquivalentUnits: number;
  let commercialKwhPerSqft: number | null = null;
  let commercialThermsPerSqft: number | null = null;
  let commercialKwhPerEstablishment: number | null = null;
  let commercialThermsPerEstablishment: number | null = null;

  if (sqft != null && sqft > 0) {
    commercialEquivalentUnits = max1(sqft);
    commercialKwhPerSqft = comKwh / sqft;
    commercialThermsPerSqft = comTherms / sqft;
  } else if (establishments != null) {
    commercialEquivalentUnits = max1(establishments);
    commercialKwhPerEstablishment = comKwh / establishments;
    commercialThermsPerEstablishment = comTherms / establishments;
  } else {
    commercialEquivalentUnits = max1(Math.round(pop / COMMERCIAL_STOCK_POP_DIVISOR) || 1);
  }

  const avgThermsPerCommercialEquivalentUnit =
    comTherms > 0 ? comTherms / commercialEquivalentUnits : FALLBACK_COM_THERMS_PER_EQUIVALENT_UNIT;
  const avgKwhPerCommercialEquivalentUnit =
    comKwh > 0 ? comKwh / commercialEquivalentUnits : FALLBACK_COM_KWH_PER_EQUIVALENT_UNIT;

  const residentialThermsPerHousehold =
    hasGas && resTherms > 0 ? resTherms / housingUnits : FALLBACK_RES_THERMS_PER_HOUSEHOLD;
  const residentialKwhPerHousehold =
    hasElec && resKwh > 0 ? resKwh / housingUnits : FALLBACK_RES_KWH_PER_HOUSEHOLD;

  const dataSource: 'location' | 'fallback' =
    hasGas || hasElec ? 'location' : 'fallback';

  return {
    referenceYear,
    dataSource,
    housingUnits,
    residentialKwhPerHousehold,
    residentialThermsPerHousehold,
    commercialKwhPerSqft,
    commercialThermsPerSqft,
    commercialKwhPerEstablishment,
    commercialThermsPerEstablishment,
    commercialEquivalentUnits,
    avgThermsPerCommercialEquivalentUnit: Math.max(1, avgThermsPerCommercialEquivalentUnit),
    avgKwhPerCommercialEquivalentUnit: Math.max(1, avgKwhPerCommercialEquivalentUnit),
  };
}
